import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Lock, Users, ChatCircle, Crown, PaintBrush, GameController, Trophy, Heart } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { WelcomeBackButton } from '../components/WelcomeBackButton';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [confessionOfDay, setConfessionOfDay] = useState(null);
  const [confessionOfWeek, setConfessionOfWeek] = useState(null);

  useEffect(() => {
    fetchFeatured();
  }, []);

  const fetchFeatured = async () => {
    try {
      const [dayRes, weekRes] = await Promise.all([
        axios.get(`${API}/thoughts/confession-of-the-day`),
        axios.get(`${API}/thoughts/confession-of-the-week`)
      ]);
      setConfessionOfDay(dayRes.data);
      setConfessionOfWeek(weekRes.data);
    } catch (error) {
      console.error('Failed to fetch featured confessions:', error);
    }
  };

  return (
    <div className="min-h-screen pb-20" data-testid="home-page">
      <div
        className="relative min-h-[85vh] md:min-h-[90vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/7505924/pexels-photo-7505924.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/70" />

        <div className="relative z-10 text-center max-w-5xl mx-auto px-4 sm:px-6">
          <p className="uppercase text-[10px] sm:text-xs tracking-[0.2em] font-medium text-muted-foreground mb-4 md:mb-6" data-testid="hero-overline">WHERE TRUTH SPEAKS ANONYMOUSLY</p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none font-light mb-4 md:mb-6" data-testid="hero-heading">
            Confess Anonymously <span className="text-primary font-normal">Anytime</span>
            <br />No Name, No Judgement
          </h1>
          <p className="text-sm md:text-base leading-relaxed tracking-wide text-muted-foreground max-w-2xl mx-auto mb-8 md:mb-12" data-testid="hero-description">
            Write the confession you have never said out loud, join a live chat room, and read what
            thousands confessed today &mdash; all without revealing who you are.
          </p>

          {user ? (
            <div className="flex flex-col items-center gap-6">
              <WelcomeBackButton onPress={() => navigate('/members?tab=games')} />
              <Link to="/members" data-testid="hero-cta-members">
                <Button size="lg" variant="outline" className="rounded-full border-white/20 text-base md:text-lg px-6 md:px-8 py-5 md:py-6">
                  Write a Confession
                </Button>
              </Link>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link to="/register" className="w-full sm:w-auto" data-testid="hero-cta-register">
                <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-base md:text-lg px-6 md:px-8 py-5 md:py-6 w-full">
                  Share Your First Confession
                </Button>
              </Link>
              <Link to="/login" className="w-full sm:w-auto" data-testid="hero-cta-login">
                <Button size="lg" variant="outline" className="rounded-full border-white/20 text-base md:text-lg px-6 md:px-8 py-5 md:py-6 w-full">
                  Sign In &amp; Confess
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-16 md:py-24 lg:py-32">
        <div className="text-center mb-10 md:mb-16">
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground mb-4">WHY CHOOSE US</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight font-light">Features That Matter</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div className="bg-card border border-border p-6 md:p-8 hover:-translate-y-1 transition-transform duration-300" data-testid="feature-anonymous">
            <Lock size={40} weight="duotone" className="text-primary mb-4" />
            <h3 className="text-xl sm:text-2xl md:text-3xl tracking-tight mb-3 md:mb-4 font-light">Anonymous Chat</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Connect with others without revealing your identity. Your privacy is our priority.
            </p>
          </div>

          <div className="bg-card border border-border p-6 md:p-8 hover:-translate-y-1 transition-transform duration-300" data-testid="feature-community">
            <Users size={40} weight="duotone" className="text-primary mb-4" />
            <h3 className="text-xl sm:text-2xl md:text-3xl tracking-tight mb-3 md:mb-4 font-light">Vibrant Community</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Join chat rooms, share thoughts, and be part of a supportive community.
            </p>
          </div>

          <div className="bg-card border border-border p-6 md:p-8 hover:-translate-y-1 transition-transform duration-300" data-testid="feature-moderation">
            <ChatCircle size={40} weight="duotone" className="text-primary mb-4" />
            <h3 className="text-xl sm:text-2xl md:text-3xl tracking-tight mb-3 md:mb-4 font-light">AI Moderation</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Advanced AI keeps conversations safe and respectful for everyone.
            </p>
          </div>
        </div>
      </div>

      <div className="w-full bg-secondary py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-10 md:mb-16">
            <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground mb-4">FEATURED CONFESSIONS</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight font-light">Truth of the Day &amp; Week</h2>
            <p className="text-sm md:text-base leading-relaxed text-muted-foreground max-w-2xl mx-auto mt-4">
              Each week&rsquo;s most-liked confession is chosen as our top confession &mdash; rewards coming soon. Share your truth today!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="featured-confessions-grid">
            {confessionOfDay && (
              <Card className="p-8 border-primary/40" data-testid="confession-of-day-card">
                <div className="flex items-center space-x-2 mb-4">
                  <Trophy size={24} weight="fill" className="text-primary" />
                  <span className="uppercase text-xs tracking-[0.2em] font-medium text-primary">Confession of the Day</span>
                </div>
                <h3 className="text-2xl tracking-tight font-light mb-3" data-testid="confession-day-title">
                  {confessionOfDay.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed" data-testid="confession-day-content">
                  {confessionOfDay.content}
                </p>
                <div className="flex items-center justify-between text-xs pt-4 border-t border-border">
                  <span className="text-muted-foreground">
                    By: <span className="text-primary">{confessionOfDay.author_name}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Heart size={14} weight="fill" className="text-primary" />
                    <span data-testid="confession-day-likes">{confessionOfDay.likes} likes</span>
                  </span>
                </div>
              </Card>
            )}

            {confessionOfWeek && (
              <Card className="p-8 border-primary shadow-[0_0_30px_rgba(225,29,72,0.3)]" data-testid="confession-of-week-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Trophy size={24} weight="fill" className="text-primary" />
                    <span className="uppercase text-xs tracking-[0.2em] font-medium text-primary">Confession of the Week</span>
                  </div>
                  <span className="text-xs bg-primary text-primary-foreground px-3 py-1 rounded-full font-medium" data-testid="confession-week-prize">
                    Reward Coming Soon
                  </span>
                </div>
                <h3 className="text-2xl tracking-tight font-light mb-3" data-testid="confession-week-title">
                  {confessionOfWeek.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed" data-testid="confession-week-content">
                  {confessionOfWeek.content}
                </p>
                <div className="flex items-center justify-between text-xs pt-4 border-t border-primary/30">
                  <span className="text-muted-foreground">
                    Top confession: <span className="text-primary font-medium">{confessionOfWeek.author_name}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Heart size={14} weight="fill" className="text-primary" />
                    <span data-testid="confession-week-likes">{confessionOfWeek.likes} likes</span>
                  </span>
                </div>
              </Card>
            )}
          </div>

          {!confessionOfDay && !confessionOfWeek && (
            <p className="text-center text-sm text-muted-foreground" data-testid="no-featured-confessions">
              Be the first to share your truth and win!
            </p>
          )}
        </div>
      </div>

      <div className="w-full bg-secondary py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground mb-4">EXCLUSIVE BENEFITS</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight font-light">Members Area Features</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link
              to={user ? '/members?tab=games' : '/register'}
              className="group bg-card/50 backdrop-blur border border-border p-6 hover:border-amber-400/50 hover:-translate-y-1 transition-all duration-300"
              data-testid="feature-games"
            >
              <GameController size={32} weight="duotone" className="text-amber-300 mb-3" />
              <h4 className="text-xl tracking-tight mb-2 font-light">Join Lots of Games</h4>
              <p className="text-xs text-muted-foreground">
                2048, Hungry Snake, mazes, image puzzles, Connect Four vs AI and AI-hosted word games &mdash; with leaderboards
              </p>
              <span className="inline-block mt-3 text-xs text-amber-300 group-hover:translate-x-1 transition-transform">
                Enter the arcade &rarr;
              </span>
            </Link>

            <div className="bg-card/50 backdrop-blur border border-border p-6" data-testid="feature-draw">
              <PaintBrush size={32} weight="duotone" className="text-primary mb-3" />
              <h4 className="text-xl tracking-tight mb-2 font-light">Drawing Board</h4>
              <p className="text-xs text-muted-foreground">Express yourself through creative drawings</p>
            </div>

            <div className="bg-card/50 backdrop-blur border border-border p-6" data-testid="feature-thoughts">
              <ChatCircle size={32} weight="duotone" className="text-primary mb-3" />
              <h4 className="text-xl tracking-tight mb-2 font-light">Share Thoughts</h4>
              <p className="text-xs text-muted-foreground">Post and read anonymous thoughts</p>
            </div>

            <div className="bg-card/50 backdrop-blur border border-border p-6" data-testid="feature-elite">
              <Crown size={32} weight="duotone" className="text-primary mb-3" />
              <h4 className="text-xl tracking-tight mb-2 font-light">Elite Membership</h4>
              <p className="text-xs text-muted-foreground">Unlimited access to all features</p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight font-light mb-6">Ready to Get It Off Your Chest?</h2>
        <p className="text-sm md:text-base leading-relaxed text-muted-foreground mb-8">Join our community today and share your first anonymous confession.</p>
        {!user && (
          <Link to="/register" data-testid="cta-register-bottom">
            <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-lg px-8 py-6">
              Share Your First Confession
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}